/**
 * 目录格式选择对话框
 * 支持系统预设格式和用户自定义格式
 */
import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from './ConfirmDialog';

export interface OutlineFormat {
  id: string;
  name: string;
  levels: string[];
  example: {
    level1: string;
    level2: string;
    level3: string;
    level4?: string;
    level5?: string;
    level6?: string;
  };
  isSystem?: boolean;  // 是否系统预设
  formatId?: string;   // 数据库ID（用于自定义格式）
}

// 系统预设格式（从数据库加载或使用默认值）
export const SYSTEM_OUTLINE_FORMATS: OutlineFormat[] = [
  {
    id: 'default',
    name: '默认格式（AI智能生成）',
    levels: ['智能', '识别', '生成', '自动', '适配', '扩展'],
    example: {
      level1: '根据招标文件自动识别',
      level2: '保持原有层级结构',
      level3: '灵活适配章节深度',
      level4: '支持四级目录结构',
      level5: '支持五级目录结构',
      level6: '支持六级目录结构',
    },
    isSystem: true,
  },
  {
    id: 'format1',
    name: '中文序号（一层）',
    levels: ['一、', '（一）', '1.', '1.1.', '1.1.1.', '1.1.1.1.'],
    example: {
      level1: '一、项目概述',
      level2: '（一）项目背景',
      level3: '1. 需求分析',
      level4: '1.1. 细节分析',
      level5: '1.1.1. 深入分析',
      level6: '1.1.1.1. 最终细节',
    },
    isSystem: true,
  },
  {
    id: 'format2',
    name: '中文+数字+括号',
    levels: ['一、', '1.', '（1）', '1.1.', '1.1.1.', '1.1.1.1.'],
    example: {
      level1: '一、技术方案',
      level2: '1. 系统架构',
      level3: '（1）架构设计',
      level4: '1.1. 设计细节',
      level5: '1.1.1. 深入设计',
      level6: '1.1.1.1. 最终设计',
    },
    isSystem: true,
  },
  {
    id: 'format3',
    name: '括号+数字',
    levels: ['（一）', '1.', '（1）', '1.1.', '1.1.1.', '1.1.1.1.'],
    example: {
      level1: '（一）实施方案',
      level2: '1. 实施步骤',
      level3: '（1）准备阶段',
      level4: '1.1. 阶段细节',
      level5: '1.1.1. 深入阶段',
      level6: '1.1.1.1. 最终阶段',
    },
    isSystem: true,
  },
  {
    id: 'format4',
    name: '第X章+中文+数字',
    levels: ['第一章、', '一、', '1.', '1.1.', '1.1.1.', '1.1.1.1.'],
    example: {
      level1: '第一章、项目总述',
      level2: '一、项目目标',
      level3: '1. 核心目标',
      level4: '1.1. 目标细节',
      level5: '1.1.1. 深入目标',
      level6: '1.1.1.1. 最终目标',
    },
    isSystem: true,
  },
  {
    id: 'format5',
    name: '数字层级',
    levels: ['1.', '1.1.', '1.1.1.', '1.1.1.1.', '1.1.1.1.1.', '1.1.1.1.1.1.'],
    example: {
      level1: '1. 项目管理',
      level2: '1.1. 管理体系',
      level3: '1.1.1. 组织架构',
      level4: '1.1.1.1. 部门设置',
      level5: '1.1.1.1.1. 岗位分工',
      level6: '1.1.1.1.1.1. 职责明细',
    },
    isSystem: true,
  },
  {
    id: 'format6',
    name: '第X章+第X节+第X条',
    levels: ['第一章、', '第一节、', '第一条、', '1.', '1.1.', '1.1.1.'],
    example: {
      level1: '第一章、总则',
      level2: '第一节、编制依据',
      level3: '第一条、法律依据',
      level4: '1. 细节内容',
      level5: '1.1. 更深细节',
      level6: '1.1.1. 最终细节',
    },
    isSystem: true,
  },
  {
    id: 'format7',
    name: '第X章+数字层级',
    levels: ['第一章、', '1.1.', '1.1.1.', '1.1.1.1.', '1.1.1.1.1.', '1.1.1.1.1.1.'],
    example: {
      level1: '第一章、项目实施',
      level2: '1.1. 实施计划',
      level3: '1.1.1. 时间安排',
      level4: '1.1.1.1. 详细安排',
      level5: '1.1.1.1.1. 具体时间点',
      level6: '1.1.1.1.1.1. 最终时间点',
    },
    isSystem: true,
  },
];

interface OutlineFormatDialogProps {
  isOpen: boolean;
  currentFormat?: string;
  onConfirm: (format: OutlineFormat) => void;
  onCancel: () => void;
}

const OutlineFormatDialog: React.FC<OutlineFormatDialogProps> = ({
  isOpen,
  currentFormat = 'format1',
  onConfirm,
  onCancel,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<string>(currentFormat);
  const [allFormats, setAllFormats] = useState<OutlineFormat[]>(SYSTEM_OUTLINE_FORMATS);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, msg });
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  };
  const [editingFormat, setEditingFormat] = useState<OutlineFormat | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; formatId: string; name: string }>({
    isOpen: false,
    formatId: '',
    name: '',
  });

  // 表单状态
  const [formData, setFormData] = useState({
    name: '',
    level1: '',
    level2: '',
    level3: '',
    level4: '',
    level5: '',
    level6: '',
    example1: '',
    example2: '',
    example3: '',
    example4: '',
    example5: '',
    example6: '',
  });

  // 当 currentFormat 改变时，同步更新选中的格式
  useEffect(() => {
    setSelectedFormat(currentFormat);
  }, [currentFormat]);

  // 从后端加载自定义格式
  useEffect(() => {
    if (isOpen) {
      loadCustomFormats();
    }
  }, [isOpen]);

  const loadCustomFormats = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/custom-outline-formats/`);
      if (response.ok) {
        const customFormats = await response.json();
        // 合并系统格式和自定义格式
        const merged = [...SYSTEM_OUTLINE_FORMATS];
        customFormats.forEach((fmt: any) => {
          if (!fmt.is_system) {
            merged.push({
              id: fmt.format_id,
              formatId: fmt.format_id,
              name: fmt.name,
              levels: [fmt.level1_format, fmt.level2_format, fmt.level3_format, fmt.level4_format, fmt.level5_format, fmt.level6_format].filter(Boolean),
              example: {
                level1: fmt.example_level1,
                level2: fmt.example_level2,
                level3: fmt.example_level3,
                level4: fmt.example_level4,
                level5: fmt.example_level5,
                level6: fmt.example_level6,
              },
              isSystem: false,
            });
          }
        });
        setAllFormats(merged);
      }
    } catch (error) {
      console.error('加载自定义格式失败:', error);
    }
  };

  const handleConfirm = () => {
    const format = allFormats.find(f => f.id === selectedFormat);
    if (format) {
      onConfirm(format);
    }
  };

  const handleCreateFormat = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/custom-outline-formats/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          level1_format: formData.level1,
          level2_format: formData.level2,
          level3_format: formData.level3,
          level4_format: formData.level4,
          level5_format: formData.level5,
          level6_format: formData.level6,
          example_level1: formData.example1,
          example_level2: formData.example2,
          example_level3: formData.example3,
          example_level4: formData.example4,
          example_level5: formData.example5,
          example_level6: formData.example6,
        }),
      });

      if (response.ok) {
        setShowCreateDialog(false);
        resetForm();
        loadCustomFormats();
        showToast('success', '自定义格式创建成功');
      } else {
        showToast('error', '创建失败，请重试');
      }
    } catch (error) {
      console.error('创建自定义格式失败:', error);
      showToast('error', '创建失败，请重试');
    }
  };

  const handleUpdateFormat = async () => {
    if (!editingFormat?.formatId) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/custom-outline-formats/${editingFormat.formatId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          level1_format: formData.level1,
          level2_format: formData.level2,
          level3_format: formData.level3,
          level4_format: formData.level4,
          level5_format: formData.level5,
          level6_format: formData.level6,
          example_level1: formData.example1,
          example_level2: formData.example2,
          example_level3: formData.example3,
          example_level4: formData.example4,
          example_level5: formData.example5,
          example_level6: formData.example6,
        }),
      });

      if (response.ok) {
        setShowEditDialog(false);
        setEditingFormat(null);
        resetForm();
        loadCustomFormats();
        showToast('success', '格式修改成功');
      } else {
        showToast('error', '更新失败，请重试');
      }
    } catch (error) {
      console.error('更新自定义格式失败:', error);
      showToast('error', '更新失败，请重试');
    }
  };

  const handleDeleteFormat = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/api/custom-outline-formats/${confirmDelete.formatId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setConfirmDelete({ isOpen: false, formatId: '', name: '' });
        loadCustomFormats();
        showToast('success', '已删除成功');
        // 如果删除的是当前选中的格式，切换到默认格式
        if (selectedFormat === confirmDelete.formatId) {
          setSelectedFormat('default');
        }
      } else {
        showToast('error', '删除失败，请重试');
      }
    } catch (error) {
      console.error('删除自定义格式失败:', error);
      showToast('error', '删除失败，请重试');
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (format: OutlineFormat) => {
    setEditingFormat(format);
    setFormData({
      name: format.name,
      level1: format.levels[0],
      level2: format.levels[1],
      level3: format.levels[2],
      level4: format.levels[3] || '',
      level5: format.levels[4] || '',
      level6: format.levels[5] || '',
      example1: format.example.level1,
      example2: format.example.level2,
      example3: format.example.level3,
      example4: format.example.level4 || '',
      example5: format.example.level5 || '',
      example6: format.example.level6 || '',
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      level1: '',
      level2: '',
      level3: '',
      level4: '',
      level5: '',
      level6: '',
      example1: '',
      example2: '',
      example3: '',
      example4: '',
      example5: '',
      example6: '',
    });
  };

  const renderFormatForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">格式名称</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="例如：自定义格式1"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">层级格式</label>
        {/* 第一行：1-3级 */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">一级</label>
            <input
              type="text"
              value={formData.level1}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level1: value,
                  example1: value ? `${value}章节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="一、"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">二级</label>
            <input
              type="text"
              value={formData.level2}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level2: value,
                  example2: value ? `${value}节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="（一）"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">三级</label>
            <input
              type="text"
              value={formData.level3}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level3: value,
                  example3: value ? `${value} 节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="1."
            />
          </div>
        </div>
        {/* 第二行：4-6级 */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">四级</label>
            <input
              type="text"
              value={formData.level4 || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level4: value,
                  example4: value ? `${value} 节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="1.1."
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">五级</label>
            <input
              type="text"
              value={formData.level5 || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level5: value,
                  example5: value ? `${value} 节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="1.1.1."
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">六级</label>
            <input
              type="text"
              value={formData.level6 || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ 
                  ...formData, 
                  level6: value,
                  example6: value ? `${value} 节标题` : ''
                });
              }}
              className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="1.1.1.1."
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">预览效果</label>
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-2">
          <div className="text-lg font-bold text-gray-900 truncate">
            {formData.example1 || '一、章节标题'}
          </div>
          <div className="text-base font-bold text-gray-800 truncate pl-4">
            {formData.example2 || '（一）节标题'}
          </div>
          <div className="text-sm font-bold text-gray-700 truncate pl-8">
            {formData.example3 || '1. 节标题'}
          </div>
          {formData.level4 && (
            <div className="text-sm text-gray-600 truncate pl-12">
              {formData.example4 || '1.1. 节标题'}
            </div>
          )}
          {formData.level5 && (
            <div className="text-sm text-gray-500 truncate pl-16">
              {formData.example5 || '1.1.1. 节标题'}
            </div>
          )}
          {formData.level6 && (
            <div className="text-sm text-gray-400 truncate pl-20">
              {formData.example6 || '1.1.1.1. 节标题'}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Toast 提示 */}
      {toast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2 px-5 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          toast.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {toast.type === 'success' ? (
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.msg}
        </div>
      )}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onCancel}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[85vh] flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      选择目录格式
                    </Dialog.Title>
                  </div>

                  <div className="mt-4 flex-1 overflow-hidden flex flex-col">
                    <p className="text-sm text-gray-500 mb-4">
                      请选择适合您标书的章节序号格式，AI将根据所选格式重新生成目录结构
                    </p>

                    {/* 格式选择网格 - 2列布局让卡片更宽 */}
                    <div className="grid grid-cols-3 gap-3 mb-4 flex-1 overflow-y-auto">
                      {allFormats.map((format) => {
                        const isSelected = selectedFormat === format.id;
                        const isCustom = !format.isSystem;
                        
                        return (
                          <div
                            key={format.id}
                            onClick={() => setSelectedFormat(format.id)}
                            className={`group relative cursor-pointer rounded-lg border-2 p-3 transition-all hover:shadow-md ${
                              isSelected
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {/* 选中标记 */}
                            {isSelected && (
                              <div className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 z-20">
                                <CheckIcon className="h-4 w-4 text-white" />
                              </div>
                            )}

                            {/* 编辑和删除按钮 - 悬停时显示 */}
                            {isCustom && (
                              <div className="absolute top-2 right-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditDialog(format);
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                  title="编辑格式"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDelete({
                                      isOpen: true,
                                      formatId: format.formatId!,
                                      name: format.name,
                                    });
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                  title="删除格式"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            )}

                            {/* 格式名称 */}
                            <div className="mb-2">
                              <h4 className={`text-xs font-semibold ${
                                isSelected ? 'text-green-700' : 'text-gray-700'
                              }`}>
                                {format.name}
                              </h4>
                            </div>

                            {/* 示例展示 */}
                            <div className="space-y-1 bg-white rounded p-2 border border-gray-100">
                              <div className="text-xs text-gray-800 font-medium truncate">
                                {format.example.level1}
                              </div>
                              <div className="text-xs text-gray-600 pl-2 truncate">
                                {format.example.level2}
                              </div>
                              <div className="text-xs text-gray-500 pl-4 truncate">
                                {format.example.level3}
                              </div>
                              {format.example.level4 && (
                                <div className="text-xs text-gray-500 pl-6 truncate">
                                  {format.example.level4}
                                </div>
                              )}
                              {format.example.level5 && (
                                <div className="text-xs text-gray-400 pl-8 truncate">
                                  {format.example.level5}
                                </div>
                              )}
                              {format.example.level6 && (
                                <div className="text-xs text-gray-400 pl-10 truncate">
                                  {format.example.level6}
                                </div>
                              )}
                            </div>

                            {/* 底部信息：序号格式 + 自定义标签 */}
                            <div className="mt-1.5 flex items-center justify-between">
                              <span className="text-xs text-gray-400 truncate">
                                {format.levels.join(' → ')}
                              </span>
                              {isCustom && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                                  自定义
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* 创建自定义格式卡片 */}
                      <div
                        onClick={openCreateDialog}
                        className="group relative cursor-pointer rounded-lg border-2 border-dashed border-gray-300 p-3 transition-all hover:border-blue-400 hover:bg-blue-50 flex flex-col items-center justify-center min-h-[120px]"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                            <PlusIcon className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
                          </div>
                          <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                            创建自定义格式
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 底部按钮 */}
                  <div className="mt-6 flex justify-end gap-3 flex-shrink-0">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={onCancel}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={handleConfirm}
                    >
                      确认
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 创建自定义格式对话框 */}
      <Transition appear show={showCreateDialog} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowCreateDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[85vh] flex flex-col">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4 flex-shrink-0"
                  >
                    创建自定义目录格式
                  </Dialog.Title>

                  <div className="overflow-y-auto flex-1 pr-2">
                    {renderFormatForm()}
                  </div>

                  <div className="mt-4 flex justify-end gap-3 flex-shrink-0 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowCreateDialog(false)}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      onClick={handleCreateFormat}
                      disabled={!formData.name || !formData.level1 || !formData.level2 || !formData.level3}
                    >
                      创建
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 编辑自定义格式对话框 */}
      <Transition appear show={showEditDialog} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowEditDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all max-h-[85vh] flex flex-col">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4 flex-shrink-0"
                  >
                    编辑自定义目录格式
                  </Dialog.Title>

                  <div className="overflow-y-auto flex-1 pr-2">
                    {renderFormatForm()}
                  </div>

                  <div className="mt-4 flex justify-end gap-3 flex-shrink-0 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowEditDialog(false)}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      onClick={handleUpdateFormat}
                      disabled={!formData.name || !formData.level1 || !formData.level2 || !formData.level3}
                    >
                      保存
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title="确认删除"
        message={`确定要删除「${confirmDelete.name}」这个自定义格式吗？\n\n此操作不可撤销。`}
        onConfirm={handleDeleteFormat}
        onCancel={() => setConfirmDelete({ isOpen: false, formatId: '', name: '' })}
      />
    </>
  );
};

export default OutlineFormatDialog;
